import IdsHelper from '@d-shared/helpers/ids-helper/ids-helper';
import trackingService from '@d-services/tracking-service/tracking-service';
import { pascalToSnakeCase } from '@d-shared/utils/pascal-to-snake-case';
import { addObjectToFormData } from '@d-shared/forms/forms';
import Window from '@d-shared/utils/window';

const TRACKING_PARAM = 'tracking_chain_uuid';

export class EventsTracking {
  constructor() {
    this._pageEventDate = Date.now();
    this._pageEvent = null;
    this._refererUrl = null;
    this._deviceFingerprint = null;
    this._userUuid = null;
    this._parentUuid = null;

    this._extraParentParams = {};

    this._storedEvents = {};

    this._trackingEvent = null;
    this.currentChainId = null;

    this.sendEvent = this.sendEvent.bind(this);

    this._init();
  }

  _init() {
    this._collectInitialData();
    this._sendParentEvent();
  }

  _collectInitialData() {
    const trackingElement = document.querySelector('#tracking-element');

    if (!trackingElement) {
      return;
    }

    this._deviceFingerprint = this._getParamValue(trackingElement.dataset.deviceFingerprint);
    this._pageEvent = this._getParamValue(trackingElement.dataset.pageEvent);
    this._refererUrl = this._getParamValue(trackingElement.dataset.refererUrl);
    this._userUuid = this._getParamValue(trackingElement.dataset.userUuid);
    this._parentUuid = IdsHelper.getNewUuid();

    const extra = this._getParamValue(trackingElement.dataset.extra);

    if (extra) {
      const extraParams = JSON.parse(extra);

      Object.entries(extraParams).forEach(([ key, value ]) => {
        this._extraParentParams[key] = value;
      });
    }

    trackingElement.remove();
  }

  _getParamValue(param) {
    return param === 'undefined' || !param ? null : `${param}`;
  }

  _sendParentEvent() {
    if (!this._pageEvent) {
      return;
    }

    const formData = new FormData();

    const dataObject = {
      name: this._pageEvent,
      happened_at_epoch: this._pageEventDate,
      device_fingerprint: this._deviceFingerprint,
      user_uuid: this._userUuid,
      referer_url: this._refererUrl,
      uuid: this._parentUuid,
    };

    addObjectToFormData(dataObject, formData);
    addObjectToFormData(this._extraParentParams, formData);

    trackingService.sendEvent(formData);
  }

  _addDataToObject(targetObject, key, value) {
    const formattedKey = pascalToSnakeCase(key);
    targetObject[formattedKey] = value;
  }

  _formatContext(context = {}) {
    const formattedObject = {};

    if (!context) return formattedObject;

    Object.keys(context).forEach((key) => {
      this._addDataToObject(formattedObject, key, context[key]);
    });

    return formattedObject;
  }

  _manageEventObject(name, context, callback) {
    this._trackingEvent = this._prepareEventObject(name, this.currentChainId, context);

    this.sendEvent();

    if (!callback) return;
    callback();
  }

  _prepareEventObject(name, uuid, context) {
    return Object.assign(
      {
        name,
        happened_at_epoch: Date.now(),
        chain_uuid: uuid,
      },
      this._formatContext(context),
    );
  }

  sendEvent(eventUuid) {
    if (!this._parentUuid) {
      return;
    }

    const formData = new FormData();

    const dataObject = {
      parent_uuid: this._parentUuid,
      device_fingerprint: this._deviceFingerprint,
      user_uuid: this._userUuid,
    };

    const event = eventUuid ? this._storedEvents[eventUuid] : this._trackingEvent;

    if (!event) {
      return;
    }

    Object.entries(event).forEach(([ chainKey, chainValue ]) => {
      dataObject[chainKey] = chainValue;
    });

    addObjectToFormData(dataObject, formData);
    const result = trackingService.sendEvent(formData);

    if (!result) return;

    if (eventUuid) {
      delete this._storedEvents[eventUuid];
      return;
    }

    this._trackingEvent = null;
  }

  getNewChainId() {
    this.currentChainId = IdsHelper.getNewUuid();
    return this.currentChainId;
  }

  storeEvent(name, context) {
    const uuid = IdsHelper.getNewUuid();
    this._storedEvents[uuid] = this._prepareEventObject(name, uuid, context);

    return uuid;
  }

  startNewChain(name, context, callback) {
    this.currentChainId = IdsHelper.getNewUuid();
    this._manageEventObject(name, context, callback);
  }

  continueChain(name, context, callback) {
    if (!this.currentChainId) {
      this.startNewChain(name, context, callback);
      return;
    }

    this._manageEventObject(name, context, callback);
  }

  trackAndRedirect(name, context, url, forceNewChain = false) {
    if (forceNewChain) {
      this.startNewChain(name, context);
    } else {
      this.continueChain(name, context);
    }

    const urlWithExtraParam = new URL(url, window.location.origin);
    const params = new URLSearchParams(urlWithExtraParam.search);
    params.append(TRACKING_PARAM, this.currentChainId);
    urlWithExtraParam.search = params.toString();

    const redirectUrl = this._pageEvent ? urlWithExtraParam.toString() : url;

    Window.goTo(redirectUrl);
  }

  trackAndSubmitForm(name, context, formEl) {
    this.continueChain(name, context);

    if (!formEl) {
      return true;
    }

    const input = document.createElement('input');
    input.hidden = true;
    input.name = TRACKING_PARAM;
    input.value = this.currentChainId;

    const form = formEl.closest('form');
    form.appendChild(input);

    return true;
  }
}


export default {
  install: function(Vue, options) {
    Vue.prototype.eventsTracking = new EventsTracking();
  },
};
